import React, { useState } from 'react';

function ContactUs() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (response.ok) {
        setStatus('Message sent successfully!');
        setForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      } else {
        setStatus(`Error: ${data.error}`);
      }
    } catch (error) {
      setStatus('Error sending message. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary text-center mb-4 sm:mb-6 md:mb-8">Contact Us</h1>
        <p className="text-gray-600 text-center mb-6 sm:mb-8 md:mb-12 max-w-2xl mx-auto text-sm sm:text-base md:text-lg">
          Our dedicated team is here to assist you with expert veterinary care, farm management, or emergency support. Reach out today!
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          {/* Contact Information & Map */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-primary mb-4 sm:mb-6 text-center">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
              <div className="min-w-0 p-4 sm:p-6 bg-primary text-white rounded-lg hover:bg-opacity-90 transition duration-200 text-center">
                <svg className="w-6 sm:w-8 h-6 sm:h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1.45 1 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                <h3 className="text-base sm:text-lg font-semibold">Phone</h3>
                <p className="text-sm sm:text-base"><a href="tel:+12345678900" className="text-secondary hover:underline">+44 161 566 9077</a></p>
              </div>
              <div className="min-w-0 p-4 sm:p-6 bg-primary text-white rounded-lg hover:bg-opacity-90 transition duration-200 text-center">
                <svg className="w-6 sm:w-8 h-6 sm:h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <h3 className="text-base sm:text-lg font-semibold">Email</h3>
                <p className="text-sm sm:text-base break-words"><a href="mailto:info@chickfarm.site " className="text-secondary hover:underline">info@chickfarm.site </a></p>
              </div>
              <div className="min-w-0 p-4 sm:p-6 bg-primary text-white rounded-lg hover:bg-opacity-90 transition duration-200 text-center">
                <svg className="w-6 sm:w-8 h-6 sm:h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                <h3 className="text-base sm:text-lg font-semibold">Location</h3>
                <p className="text-sm sm:text-base break-words">51 Queen Ediths Way, Cambridge, United Kingdom, CB1 8PJ</p>
              </div>
            </div>
           <a
              href="https://www.google.com/maps?q=51+Queen+Ediths+Way,+Cambridge,+CB1+8PJ"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="relative w-full h-48 sm:h-56 md:h-64 rounded-lg overflow-hidden shadow-md">
                <iframe
                  title="Map"
                  src="https://www.google.com/maps?q=51+Queen+Ediths+Way,+Cambridge,+CB1+8PJ&output=embed"
                  className="w-full h-full border-0 pointer-events-none"
                />
              </div>
            </a>

          </div>

          {/* Contact Form */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-primary mb-4 sm:mb-6">Get In Touch</h2>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm sm:text-base md:text-lg font-medium text-gray-700 mb-1 sm:mb-2">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Enter First Name"
                  />
                </div>
                <div>
                  <label className="block text-sm sm:text-base md:text-lg font-medium text-gray-700 mb-1 sm:mb-2">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Enter Last Name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm sm:text-base md:text-lg font-medium text-gray-700 mb-1 sm:mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Enter Email Address"
                  />
                </div>
                <div>
                  <label className="block text-sm sm:text-base md:text-lg font-medium text-gray-700 mb-1 sm:mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Enter Phone Number"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm sm:text-base md:text-lg font-medium text-gray-700 mb-1 sm:mb-2">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  rows="4 sm:rows-6"
                  placeholder="Enter your message"
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-secondary text-white font-semibold rounded-lg hover:bg-opacity-90 transition duration-300"
                disabled={status === 'Sending...'}
              >
                {status === 'Sending...' ? 'Sending...' : 'Submit'}
              </button>
              {status && <p className={status.includes('Error') ? 'text-red-600' : 'text-green-600 text-center mt-2 sm:mt-4 text-sm sm:text-base'}>{status}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;